import { useCallback, useEffect, useRef, useState } from 'react';

import * as Sentry from '@sentry/react';

import { vnextApiClient } from '@/services/vnext-api';
import { MonitoringAlertPage, MonitoringAlertQuery } from '@/types/monitoring';

/**
 * How often the queue re-reads. Slower than the inbox's five seconds: alerts
 * arrive when a provider's list refreshes, not when a colleague clicks
 * something, so a faster poll would spend requests to shorten a wait nobody is
 * having.
 */
const ALERT_POLL_MS = 30_000;

/**
 * The analyst alert queue, polled with ETag revalidation.
 *
 * The steady state is a 304 with no body, so an idle queue costs one aggregate
 * query per tick rather than a page read.
 *
 * The stored ETag is dropped whenever the query changes, because a different
 * filter or page is a different token space — reusing it would 304 against the
 * previous filter and leave the analyst looking at rows they just filtered
 * away, with nothing on screen to say so.
 */
export function useMonitoringAlerts(
  query: MonitoringAlertQuery = {},
  options?: { poll?: boolean; enabled?: boolean }
) {
  const [page, setPage] = useState<MonitoringAlertPage | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const key = JSON.stringify(query);
  const queryRef = useRef(query);
  queryRef.current = query;

  const etagRef = useRef<string | null>(null);
  // Stops a slow response stacking under the poll interval.
  const inFlightRef = useRef(false);

  const fetchOnce = useCallback(async () => {
    // Deliberately not gated on inFlightRef: a user-driven refetch — right
    // after adjudicating an alert, say — must never be swallowed by a poll
    // that was already in the air.
    inFlightRef.current = true;
    setIsLoading(true);
    setError(null);
    try {
      const result = await vnextApiClient.listMonitoringAlerts(queryRef.current);
      if (result !== 'not-modified') {
        etagRef.current = result.etag;
        setPage(result.data);
      }
    } catch (err) {
      const e = err instanceof Error ? err : new Error('Failed to load alerts');
      Sentry.captureException(e);
      setError(e);
    } finally {
      inFlightRef.current = false;
      setIsLoading(false);
    }
  }, []);

  const pollOnce = useCallback(async () => {
    if (inFlightRef.current) {
      return;
    }
    inFlightRef.current = true;
    try {
      const result = await vnextApiClient.listMonitoringAlerts(queryRef.current, {
        etag: etagRef.current,
      });
      if (result !== 'not-modified') {
        etagRef.current = result.etag;
        setPage(result.data);
      }
    } catch (err) {
      // A transient poll failure must not flash the page-level error banner —
      // the next tick recovers, and an analyst mid-triage should not have the
      // screen replaced by an error they cannot act on.
      Sentry.captureException(err instanceof Error ? err : new Error(String(err)));
    } finally {
      inFlightRef.current = false;
    }
  }, []);

  useEffect(() => {
    if (options?.enabled === false) {
      return undefined;
    }
    etagRef.current = null;
    void fetchOnce();
    if (options?.poll === false) {
      return undefined;
    }
    const id = setInterval(() => {
      void pollOnce();
    }, ALERT_POLL_MS);
    return () => clearInterval(id);
  }, [key, options?.poll, options?.enabled, fetchOnce, pollOnce]);

  return { page, isLoading, error, refetch: fetchOnce };
}
